import { useState } from 'react';
import type { FormEvent } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { shortDate, today } from '@/lib/format';
import { useCreateSupervisionLog, useSiteVendors, useSupervisionLogs } from '@/lib/queries';
import { useSite } from '@/lib/useSite';

/**
 * 감리일지.
 *
 * <p>감리단이 쓰고, <b>협력사는 읽는다</b> — 「지시사항」이 협력사에게 닿아야 하는 칸이라
 * 감리 전용으로 감추지 않는다. 작업일보(협력사가 쓰고 현장이 제출)와 짝을 이룬다.
 *
 * <p><b>수신 업체</b>는 선택이다(사용자 결정 2026-09-10 · DOC-84 §7 ⑤). 비우면 현장의 모든
 * 협력사가 본다(기본). 고르면 그 업체와 감리만 본다 — 「이 업체만 보라」 는 지시가 실제로 있다.
 */
export function SupervisionLogPage() {
  const { siteId, siteName, readOnly, partyType } = useSite();
  const logs = useSupervisionLogs(siteId);
  const vendors = useSiteVendors(siteId);
  const create = useCreateSupervisionLog(siteId);

  const [open, setOpen] = useState(false);
  const [logDate, setLogDate] = useState(today);
  const [weather, setWeather] = useState('');
  const [progressNote, setProgressNote] = useState('');
  const [instruction, setInstruction] = useState('');
  const [remark, setRemark] = useState('');
  const [targetVendorId, setTargetVendorId] = useState('');

  const rows = logs.data ?? [];
  const vendorRows = vendors.data ?? [];
  const canWrite = !readOnly && partyType === 'SUPERVISOR';

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await create.mutateAsync({
      logDate,
      weather: weather.trim() === '' ? null : weather.trim(),
      progressNote: progressNote.trim() === '' ? null : progressNote.trim(),
      instruction: instruction.trim() === '' ? null : instruction.trim(),
      remark: remark.trim() === '' ? null : remark.trim(),
      targetVendorId: targetVendorId === '' ? null : targetVendorId,
    });
    setOpen(false);
    setProgressNote('');
    setInstruction('');
    setRemark('');
    setTargetVendorId('');
  }

  return (
    <>
      <PageHeader
        title="감리일지"
        subtitle={siteName}
        action={
          canWrite && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              작성
            </button>
          )
        }
      />

      {open && (
        <form onSubmit={onSubmit} className="card mb-3">
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>날짜</span>
              <input type="date" className="input-field" value={logDate} onChange={(e) => setLogDate(e.target.value)} required />
            </label>
            <label className="w-28">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>날씨</span>
              <input type="text" className="input-field" value={weather} onChange={(e) => setWeather(e.target.value)} placeholder="맑음" />
            </label>
          </div>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>공사 현황</span>
            <textarea className="input-field" rows={3} value={progressNote} onChange={(e) => setProgressNote(e.target.value)} />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>지시사항</span>
            <textarea className="input-field" rows={3} value={instruction} onChange={(e) => setInstruction(e.target.value)} />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>수신 업체</span>
            <select className="input-field" value={targetVendorId} onChange={(e) => setTargetVendorId(e.target.value)}>
              <option value="">현장 전체</option>
              {vendorRows.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.mainTrade === null ? it.name : `${it.name} · ${it.mainTrade}`}
                </option>
              ))}
            </select>
          </label>
          <label className="mb-4 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>특기사항</span>
            <textarea className="input-field" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
              {create.isPending ? '보내는 중입니다…' : '저장'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={logs.isPending}
        error={logs.isError}
        empty={rows.length === 0}
        emptyText="작성된 감리일지가 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="text-ui-md font-medium">{shortDate(row.logDate)}</span>
                  {row.targetVendorName !== null && (
                    <span className="badge badge-blue truncate">{row.targetVendorName}</span>
                  )}
                </div>
                <div className="shrink-0 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {[row.weather, row.author].filter(Boolean).join(' · ')}
                </div>
              </div>

              {row.progressNote !== null && <p className="mb-2 text-ui-sm">{row.progressNote}</p>}

              {row.instruction !== null && (
                <div className="rounded-md p-2" style={{ background: 'var(--accent-bg)' }}>
                  <div className="mb-0.5 text-ui-2xs font-medium" style={{ color: 'var(--accent)' }}>지시사항</div>
                  <p className="text-ui-sm">{row.instruction}</p>
                </div>
              )}

              {row.remark !== null && (
                <p className="mt-2 text-ui-xs" style={{ color: 'var(--text-muted)' }}>{row.remark}</p>
              )}
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
